import {
  type TableColumnFormatType,
  type TableColumnHashingAlgorithm,
} from "@decentriq/graphql/dist/types";
import { type ab_media_response as ddcAbMediaResponse } from "ddc";
import { createContext } from "react";
import {
  type MediaDataRoomParticipant,
  type MediaDataRoomVersion,
} from "features/mediaDataRoom/models";

export interface MediaDataRoomContextValue {
  dataRoomName: string;
  loading: boolean;
  dataRoomId: string;
  driverAttestationHash: string;
  isDeactivated: boolean;
  isStopped: boolean;
  baseAudienceProviderName: string;
  seedAudienceProviderName: string;
  supportedFeatures: {
    performanceReportEnabled: boolean;
    showAbsoluteValues: boolean;
    canCreateAudience: boolean;
    canProvideSeedAudience: boolean;
    canProvideBaseAudience: boolean;
    canExportAudience: boolean;
    enableRemarketing: boolean;
    enableInsights: boolean;
    enableDropInvalidRows: boolean;
    enableLookalike: boolean;
    enableRuleBasedAudiences: boolean;
    canViewAudiences: boolean;
    canViewInsights: boolean;
    canDeleteAudience: boolean;
    canViewSeedAudienceDetails: boolean;
    canViewBaseAudienceDetails: boolean;
    canShareAudience: boolean;
    canViewAudienceStatus: boolean;
    canStopMediaDataRoom: boolean;
    enableOverlapStatistics: {
      enabled: boolean;
      canView: boolean;
    };
  };
  matchingIdFormat: TableColumnFormatType;
  hashMatchingIdWith: TableColumnHashingAlgorithm | null;
  createdAt: string;
  createdBy: string;
  version: MediaDataRoomVersion;
  currentUserEmail: string;
}

export type MediaDataRoomAbContextValue = Omit<
  MediaDataRoomContextValue,
  "supportedFeatures"
> & {
  rawDefinition: ddcAbMediaResponse.AbMediaDcr;
  isAdvertiser: boolean;
  isAgency: boolean;
  hasAgency: boolean;
  hasDataPartner: boolean;
  isObserver: boolean;
  isDataPartner: boolean;
  isPublisher: boolean;
  publisherLogo?: string;
  advertiserLogo?: string;
  advertiserUserEmails: string[];
  agencyUserEmails: string[];
  dataPartnerUserEmails: string[];
  observerUserEmails: string[];
  publisherUserEmails: string[];
  supportedFeatures: MediaDataRoomContextValue["supportedFeatures"] & {
    shouldFilterOutPublishedAsIntermediateAudiences: boolean;
    skipAudiencesResettingOnDataLabChange: boolean;
  };
};

export interface MediaDataRoomV3ContextValue extends MediaDataRoomContextValue {
  participants: MediaDataRoomParticipant[];
}

export interface MediaDataRoomContextHook {
  (version?: never): MediaDataRoomContextValue;
  (version: MediaDataRoomVersion.v3): MediaDataRoomV3ContextValue;
  (version: MediaDataRoomVersion.ab): MediaDataRoomAbContextValue;
}

export const MediaDataRoomContext =
  createContext<MediaDataRoomContextValue | null>(null);
MediaDataRoomContext.displayName = "MediaDataRoomContext";

export const MediaDataRoomContextConsumer = MediaDataRoomContext.Consumer;
